import { useState } from 'react';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import {
  Discount,
  DiscountDraft,
  useCreateDiscount,
  useDeleteDiscount,
  useDiscounts,
  useSyncDiscountWithStripe,
  useUpdateDiscount,
} from '../../queries/discounts';
import { DiscountFormDialog, todayDateString } from './DiscountFormDialog';
import { useToast } from '../../contexts/ToastContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { Checkbox } from '../../components/ui/Checkbox';
import { Button } from '../../components/ui/Button';
import { Badge, BadgeProps } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

// start_date is seeded rather than only defaulted in the input: the field
// showed today's date but did not put it in the draft, so an untouched date
// meant the API rejected the save for a missing start_date.
const emptyDraft = (): DiscountDraft => ({
  code: '',
  name: '',
  description: '',
  discount_type: 'PERCENTAGE',
  applies_to: 'ALL',
  stripe_duration: 'ONCE',
  usage_limit_per_user: 1,
  is_active: true,
  start_date: todayDateString(),
});

const SYNC_VARIANT: Record<string, BadgeProps['variant']> = {
  synced: 'success',
  pending: 'warning',
  error: 'destructive',
};

function formatValue(discount: Discount): string {
  if (discount.discount_type === 'percentage') return `${discount.discount_value}%`;
  if (discount.discount_type === 'fixed_amount') return `$${discount.discount_value}`;
  return `${discount.discount_value} days`;
}

// Converts a stored (lowercase) discount back into the uppercase draft shape the form expects.
function toDraft(d: Discount): DiscountDraft {
  return {
    id: d.id,
    code: d.discount_code,
    name: d.name,
    description: d.description,
    discount_type: d.discount_type.toUpperCase() as DiscountDraft['discount_type'],
    discount_value: d.discount_value,
    applies_to: d.applies_to.toUpperCase() as DiscountDraft['applies_to'],
    min_purchase_amount: d.min_purchase_amount,
    max_discount_amount: d.max_discount_amount,
    start_date: d.start_date?.split('T')[0],
    end_date: d.end_date?.split('T')[0] ?? null,
    usage_limit: d.usage_limit,
    usage_limit_per_user: d.usage_limit_per_user,
    first_time_users_only: d.first_time_users_only,
    is_active: d.is_active,
    stripe_duration: d.stripe_duration.toUpperCase() as DiscountDraft['stripe_duration'],
    // Carried through so saving an edit doesn't null the month count.
    stripe_duration_in_months: d.stripe_duration_in_months,
  };
}

export function DiscountsPage() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data: discounts = [], isLoading } = useDiscounts(includeInactive, true);
  const create = useCreateDiscount();
  const update = useUpdateDiscount();
  const remove = useDeleteDiscount();
  const sync = useSyncDiscountWithStripe();
  const { success, error } = useToast();

  const [editing, setEditing] = useState<DiscountDraft | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Discount | null>(null);

  const saving = create.isPending || update.isPending;

  const handleSave = () => {
    if (!editing) return;
    setFormError(null);
    const isEdit = !!editing.id;
    const onSettled = {
      onSuccess: () => {
        setEditing(null);
        success(isEdit ? 'Discount updated.' : 'Discount created.');
      },
      onError: (err: unknown) => setFormError(extractErrorMessage(err, 'Save failed.')),
    };
    if (editing.id) {
      update.mutate({ ...editing, id: editing.id }, onSettled);
    } else {
      create.mutate(editing, onSettled);
    }
  };

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deleted "${deleting.name}".`);
        setDeleting(null);
      },
      onError: (err: unknown) => {
        error(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  const handleSync = (discount: Discount, action: 'sync' | 'forceResync') => {
    sync.mutate(
      { discountId: discount.id, action },
      {
        onSuccess: () => success(`Synced "${discount.discount_code}" with Stripe.`),
        onError: (err: unknown) => error(extractErrorMessage(err, 'Stripe sync failed.')),
      },
    );
  };

  const columns: DataTableColumn<Discount>[] = [
    { key: 'code', header: 'Code', priority: true, render: (d) => <span className="font-mono font-medium">{d.discount_code}</span> },
    { key: 'name', header: 'Name', priority: false, render: (d) => d.name },
    { key: 'value', header: 'Value', priority: true, render: (d) => <span className="text-muted-foreground @2xl:text-foreground">{formatValue(d)}</span> },
    { key: 'uses', header: 'Uses', priority: false, alignEnd: true, render: (d) => <span className="tabular-nums">{d.current_usage_count}{d.usage_limit != null ? ` / ${d.usage_limit}` : ''}</span> },
    {
      key: 'stripe',
      header: 'Stripe',
      priority: false,
      render: (d) => (
        <Badge variant={SYNC_VARIANT[d.stripe_sync_status] ?? 'outline'} title={d.stripe_error_message ?? undefined}>
          {d.stripe_sync_status}
        </Badge>
      ),
    },
    { key: 'active', header: 'Active', priority: false, render: (d) => (d.is_active ? <Badge variant="success">Active</Badge> : <Badge variant="outline">Inactive</Badge>) },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (d) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => handleSync(d, d.stripe_sync_status === 'error' ? 'forceResync' : 'sync')} disabled={sync.isPending} title="Sync with Stripe">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setEditing(toDraft(d))} title="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleting(d)}
            disabled={d.current_usage_count > 0}
            className="text-muted-foreground hover:text-destructive"
            title={d.current_usage_count > 0 ? 'Cannot delete: has been used' : 'Delete'}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Discounts"
        description="Promo codes applied at checkout."
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <Checkbox checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
              Show inactive
            </label>
            <Button onClick={() => setEditing(emptyDraft())}>
              <Plus className="h-4 w-4" />
              Add discount
            </Button>
          </>
        }
      />

      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable columns={columns} data={discounts} getRowKey={(d) => String(d.id)} emptyMessage="No discounts yet." />
      )}

      {editing && (
        <DiscountFormDialog
          draft={editing}
          onChange={setEditing}
          onCancel={() => {
            setEditing(null);
            setFormError(null);
          }}
          onSave={handleSave}
          saving={saving}
          error={formError}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete discount?"
        description={`"${deleting?.name}" (${deleting?.discount_code}) will be permanently removed and can no longer be applied at checkout. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
